import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Mail, ChevronDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { HexGlyph } from "@/components/HexIcon";

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yHexA = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yHexB = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="grain honeycomb-light relative flex min-h-screen items-center overflow-hidden pt-24 pb-16"
    >
      {/* warm radial glow */}
      <motion.div
        style={{ opacity: glowOpacity }}
        className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(229,169,60,0.28),transparent_65%)] blur-2xl"
      />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-12%] h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(200,145,47,0.16),transparent_65%)] blur-2xl" />

      {/* floating honeycomb accents */}
      <motion.div style={{ y: yHexA }} className="pointer-events-none absolute left-[6%] top-[18%] hidden text-gold/40 md:block">
        <HexGlyph className="h-16 w-16 animate-float-slow" />
      </motion.div>
      <motion.div style={{ y: yHexB }} className="pointer-events-none absolute bottom-[14%] right-[8%] hidden text-gold/30 md:block">
        <HexGlyph className="h-24 w-24 animate-spin-slower" />
      </motion.div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.15fr_0.85fr]">
        {/* Copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-white/60 px-4 py-1.5 backdrop-blur"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 rounded-full bg-gold" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-gold-deep">
              Madison County &amp; Surrounding Areas
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Connecting</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="italic text-gold-deep">Community,</span>
            </MaskedLine>
            <MaskedLine delay={0.51}>one family at a time.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ink-soft sm:text-lg"
            data-testid="hero-tagline"
          >
            The Hive connects individuals &amp; families to services, support &amp;
            resources — right here in our community.{" "}
            <span className="font-script text-2xl text-gold-deep">You are not alone.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("#contact")}
              data-testid="hero-get-support-btn"
              className="group flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-bold text-cream transition-colors duration-300 hover:bg-gold-deep"
            >
              Get Support
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollTo("#services")}
              data-testid="hero-find-resources-btn"
              className="rounded-full border-2 border-ink/70 px-7 py-3 text-sm font-bold text-ink transition-colors duration-300 hover:border-gold-deep hover:text-gold-deep"
            >
              Find Resources
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mx-auto mt-8 flex max-w-md items-stretch justify-center gap-3"
          >
            <a
              href="tel:4794375939"
              data-testid="hero-call-btn"
              className="flex flex-1 items-center justify-center gap-2.5 rounded-full bg-gold px-5 py-3 text-sm font-bold text-white shadow-[0_12px_28px_-12px_rgba(200,145,47,0.55)] transition-all duration-300 hover:scale-[1.03] hover:bg-gold-deep"
            >
              <Phone size={17} strokeWidth={2.2} />
              Call
            </a>
            <a
              href="mailto:Themadcohive@gmail.com"
              data-testid="hero-email-btn"
              className="flex flex-1 items-center justify-center gap-2.5 rounded-full border-2 border-gold/60 bg-white/70 px-5 py-3 text-sm font-bold text-gold-deep backdrop-blur transition-all duration-300 hover:scale-[1.03] hover:border-gold-deep"
            >
              <Mail size={17} strokeWidth={2.2} />
              Email The Hive
            </a>
          </motion.div>
        </div>

        {/* Visual — hex-clipped warm photo + logo badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <motion.div style={{ y: yPhoto }} className="relative">
            <div className="clip-hex gold-ring relative aspect-[0.92] w-full overflow-hidden bg-white">
              <img
                src="/assets/hive-family.jpeg"
                alt="Silhouette of a multi-generational family holding hands"
                className="h-full w-full object-contain p-8 sm:p-10"
                data-testid="hero-image"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="absolute -bottom-8 -left-4 flex items-center gap-3 rounded-2xl border border-gold/30 bg-white/90 p-3 pr-5 shadow-[0_18px_40px_-16px_rgba(22,19,13,0.3)] backdrop-blur sm:-left-10"
            data-testid="hero-logo-badge"
          >
            <img src="/assets/hive-logo-full.png" alt="" className="h-14 w-14 rounded-xl bg-white object-contain p-0.5 ring-1 ring-gold/30" />
            <div>
              <p className="font-script text-2xl leading-none text-ink">The Hive</p>
              <p className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-deep">
                Connecting Community
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => scrollTo("#about")}
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to about section"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-gold-deep md:block"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ChevronDown size={26} />
        </motion.div>
      </motion.button>
    </section>
  );
}
